// The Q4 October target research (jobs/load_q4_targets.py) in the words the board, the panel and
// the Research feed show. Pure, so `node --test` covers it without a DOM.
//
// Every link goes through `safeHref`, although the server already keeps only absolute http(s)
// URLs: anything else renders as text.
import type { AccountSignal, FoundByScout, Q4Evidence, Q4Target } from '../api/types.ts';
import { safeHref } from '../sidebar/overviewSource.ts';
import { pacificCalendarDay } from './format.ts';

// The `found_by_scout.source` the loader writes on a Q4 hero contact.
export const Q4_HERO_SOURCE = 'Q4 research';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-04" → "4 Oct", "2026-10" → "Oct 2026", a timestamp → its Pacific day; else empty. */
export function q4DayLabel(on: string | null | undefined): string {
  if (!on) return '';

  // A month-only date has no day to show, and must not be read as the month's first.
  const monthOnly = /^(\d{4})-(\d{2})$/.exec(on);
  if (monthOnly) {
    const month = MONTHS[Number(monthOnly[2]) - 1];
    return month ? `${month} ${monthOnly[1]}` : '';
  }

  const day = /^\d{4}-(\d{2})-(\d{2})/.exec(pacificCalendarDay(on));
  if (!day) return '';
  const month = MONTHS[Number(day[1]) - 1];
  return month ? `${Number(day[2])} ${month}` : '';
}

/** The Q4 priority score as a whole number, or null when the account has no Q4 score. */
export function q4Score(target: Q4Target | null | undefined): number | null {
  if (!target || target.score == null || !Number.isFinite(target.score)) return null;
  return Math.round(target.score);
}

/** What the score is made of, for a hover; the list and rank when the score has no breakdown. */
export function q4ScoreTitle(target: Q4Target): string {
  const parts = [
    target.score_why,
    target.source_list ? `${target.source_list}${target.rank_in_list != null ? ` #${target.rank_in_list}` : ''}` : null,
  ];
  return parts.filter(Boolean).join('\n') || 'Q4 priority score';
}

/** "Omair's list"; the source list's own name when no strategist is recorded. */
export function q4ListName(target: Q4Target): string | null {
  if (target.strategist) return `${target.strategist}'s list`;
  return target.source_list;
}

/** "Q4 research · Omair's list · loaded 4 Oct". */
export function q4Heading(target: Q4Target): string {
  const loaded = q4DayLabel(target.loaded_at);
  return ['Q4 research', q4ListName(target), loaded ? `loaded ${loaded}` : null].filter(Boolean).join(' · ');
}

export interface Q4WarningReading {
  // Short flags, one chip each: "past opp", "defunct", "wrong domain?".
  headsUp: string[];
  // The free-text company flag, shown on one truncated line with the full text on hover.
  companyFlag: string | null;
}

/** The record's warnings split into chips and the one company note. Blank texts are dropped. */
export function q4Warnings(target: Q4Target | null | undefined): Q4WarningReading {
  const warnings = target?.warnings ?? [];
  const headsUp = warnings.filter(warning => warning.kind === 'heads_up').map(warning => warning.text.trim()).filter(Boolean);
  const companyFlags = warnings.filter(warning => warning.kind === 'company_flag').map(warning => warning.text.trim()).filter(Boolean);
  return { headsUp: [...new Set(headsUp)], companyFlag: companyFlags.join(' · ') || null };
}

export interface Q4ContextRow {
  label: string;
  text: string;
}

// The context fields in the order a strategist reads them, each with its label.
const CONTEXT_LABELS: Array<[keyof Q4Target['context'], string]> = [
  ['headline', 'Headline'],
  ['why_this_account', 'Why this account'],
  ['what_they_build', 'What they build'],
  ['buying_unit', 'Buying unit'],
  ['incumbent_vendor', 'Incumbent voice vendor'],
  ['angle', 'Angle'],
  ['first_touch', 'First touch'],
  ['caution', 'Caution'],
  ['voice_fit', 'Voice fit'],
  ['vertical', 'Vertical'],
];

/** The context fields that hold something, in reading order. */
export function q4ContextRows(target: Q4Target): Q4ContextRow[] {
  const rows: Q4ContextRow[] = [];
  for (const [field, label] of CONTEXT_LABELS) {
    const text = target.context?.[field]?.trim();
    if (text) rows.push({ label, text });
  }
  return rows;
}

const EVIDENCE_LABELS: Record<Q4Evidence['kind'], string> = {
  voice_evidence: 'Voice evidence',
  exa_fact: 'Exa fact',
  job_posting: 'Job posting',
};

export interface Q4EvidenceItem {
  label: string;
  text: string;
  // An http(s) link, or null: anything else stays text.
  href: string | null;
  day: string;
}

/** The cited evidence with a label, a safe link and a short date. Blank texts are dropped. */
export function q4EvidenceItems(target: Q4Target): Q4EvidenceItem[] {
  return (target.evidence ?? [])
    .filter(item => item.text?.trim())
    .map(item => ({
      label: EVIDENCE_LABELS[item.kind] ?? 'Evidence',
      text: item.text.trim(),
      href: safeHref(item.url),
      day: q4DayLabel(item.on),
    }));
}

// --- Signals and heroes ----------------------------------------------------------------------

// The two signal kinds the Q4 join adds to `account.signals`. A why-now that is a round or a hire
// arrives as `funding` / `job_posting` with the same "Q4 research: " fact prefix.
const Q4_SIGNAL_LABELS: Record<string, string> = {
  q4_research: 'Q4 research',
  q4_research_unreviewed: 'Q4 research (unreviewed Exa news)',
};
const Q4_FACT_PREFIX = /^Q4 research:\s*/i;

/** Whether a signal came from the Q4 research, by its kind or its fact's prefix. */
export function isQ4Signal(signal: Pick<AccountSignal, 'kind' | 'fact'>): boolean {
  return signal.kind in Q4_SIGNAL_LABELS || Q4_FACT_PREFIX.test(signal.fact);
}

/** "Q4 research" or "Q4 research (unreviewed Exa news)"; null for any other kind. */
export function q4SignalLabel(kind: string): string | null {
  return Q4_SIGNAL_LABELS[kind] ?? null;
}

/** The fact without its "Q4 research: " prefix, so a round or hire reads like any other. */
export function withoutQ4Prefix(fact: string): string {
  return fact.replace(Q4_FACT_PREFIX, '');
}

/** Whether a found person is a Q4 research hero rather than a Scout find. */
export function isQ4Hero(found: FoundByScout | null | undefined): boolean {
  return found?.source === Q4_HERO_SOURCE;
}
